import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenderAPI } from '../utils/api';
import { Radar, FileText, Eye, TrendingUp, Trophy, Clock, Play, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, FunnelChart, Funnel, LabelList } from 'recharts';

var FUNNEL_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#6366f1', '#8b5cf6', '#14b8a6'];
var SCORE_COLORS = { 0: '#e2e8f0', 1: '#fca5a5', 2: '#fdba74', 3: '#fcd34d', 4: '#86efac', 5: '#4ade80' };

function TenderScout() {
  var s1 = useState(null); var data = s1[0]; var setData = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(false); var scraping = s3[0]; var setScraping = s3[1];
  var s4 = useState(null); var jobId = s4[0]; var setJobId = s4[1];
  var navigate = useNavigate();

  var fetchDashboard = useCallback(async function() {
    try {
      var res = await tenderAPI.dashboard();
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(function() { fetchDashboard(); }, [fetchDashboard]);

  useEffect(function() {
    if (!jobId) return;
    var interval = setInterval(async function() {
      try {
        var res = await tenderAPI.scrapeJobStatus(jobId);
        if (res.data.status !== 'running') {
          clearInterval(interval);
          setScraping(false);
          setJobId(null);
          fetchDashboard();
        }
      } catch (e) { clearInterval(interval); setScraping(false); }
    }, 3000);
    return function() { clearInterval(interval); };
  }, [jobId, fetchDashboard]);

  var handleRunAll = async function() {
    setScraping(true);
    try {
      var res = await tenderAPI.runAll();
      if (res.data.job_id) setJobId(res.data.job_id);
    } catch (e) { console.error(e); setScraping(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!data) return <div className="p-6 text-slate-500">Failed to load dashboard</div>;

  var kpis = [
    { label: 'Total Tenders', value: data.total_tenders, icon: FileText, color: 'bg-blue-50 text-blue-600' },
    { label: 'New / Unreviewed', value: data.new, icon: Eye, color: 'bg-amber-50 text-amber-600' },
    { label: 'Qualified (3+)', value: data.qualified, icon: TrendingUp, color: 'bg-green-50 text-green-600' },
    { label: 'Pursuing', value: data.pursuing, icon: Radar, color: 'bg-primary/10 text-primary' },
    { label: 'Submitted', value: data.submitted, icon: FileText, color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Won', value: data.won, icon: Trophy, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Expired', value: data.expired, icon: Clock, color: 'bg-slate-100 text-slate-500' },
  ];

  // Build pipeline funnel data
  var funnelData = [
    { name: 'New', value: data.new || 0, fill: '#3b82f6' },
    { name: 'Reviewed', value: data.reviewed || 0, fill: '#f59e0b' },
    { name: 'Pursuing', value: data.pursuing || 0, fill: '#8b5cf6' },
    { name: 'Submitted', value: data.submitted || 0, fill: '#6366f1' },
    { name: 'Won', value: data.won || 0, fill: '#10b981' },
  ].filter(function(d) { return d.value > 0; });

  // Build score distribution from dashboard data
  var scoreDistribution = [];
  var sd = data.score_distribution || [];
  for (var sci = 0; sci < sd.length; sci++) {
    var item = sd[sci];
    var scVal = item.score !== undefined ? item.score : sci;
    scoreDistribution.push({ score: scVal + '/5', value: item.count || 0, fill: SCORE_COLORS[scVal] || '#e2e8f0' });
  }
  if (scoreDistribution.length === 0) {
    for (var sc = 0; sc <= 5; sc++) {
      scoreDistribution.push({ score: sc + '/5', value: 0, fill: SCORE_COLORS[sc] });
    }
  }

  var topItems = (data.top_tenders || []).map(function(t) {
    var scoreCls = t.final_score >= 4 ? 'bg-green-100 text-green-700' : t.final_score >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
    return (
      <div key={t._id} className="flex items-start justify-between py-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 px-2 rounded" onClick={function() { navigate('/tender-scout/tenders/' + t._id); }}>
        <div className="flex-1 min-w-0 mr-3">
          <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
          <p className="text-xs text-slate-500">{t.organisation} {t.closing_date ? '| Closes: ' + new Date(t.closing_date).toLocaleDateString() : ''}</p>
        </div>
        <span className={'text-xs font-bold px-2 py-1 rounded ' + scoreCls}>{t.final_score}/5</span>
      </div>
    );
  });

  var sourceRows = (data.source_health || []).map(function(src, si) {
    var statusCls = src.enabled ? (src.errors >= 3 ? 'text-red-600' : 'text-green-600') : 'text-slate-400';
    var statusTxt = src.enabled ? (src.errors >= 3 ? 'Error' : 'Active') : 'Disabled';
    return (
      <tr key={si} className="border-b border-slate-100">
        <td className="py-2.5 px-4 text-sm font-medium text-slate-900">{src.name}</td>
        <td className="py-2.5 px-4 text-sm"><span className={statusCls}>{statusTxt}</span></td>
        <td className="py-2.5 px-4 text-sm text-slate-600">{src.last_count || 0}</td>
        <td className="py-2.5 px-4 text-sm text-slate-500">{src.last_scraped ? new Date(src.last_scraped).toLocaleDateString() : 'Never'}</td>
      </tr>
    );
  });

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="tender-scout-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Tender Scout</h1>
          <p className="text-sm text-slate-500">Automated tender discovery and tracking</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleRunAll} disabled={scraping} className="px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 text-sm disabled:opacity-50" data-testid="run-all-scrape-btn">
            {scraping ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            {scraping ? 'Scraping...' : 'Run All Sources'}
          </button>
          <button onClick={function() { navigate('/tender-scout/tenders'); }} className="px-3 sm:px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 text-sm" data-testid="view-all-tenders-btn">
            <FileText size={16} /> View All Tenders
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {kpis.map(function(kpi) {
          var Icon = kpi.icon;
          return (
            <div key={kpi.label} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-500">{kpi.label}</span>
                <div className={'p-2 rounded-lg ' + kpi.color}><Icon size={16} /></div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Tender Pipeline Funnel</h3>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#475569' }} width={70} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                  {funnelData.map(function(entry, index) { return <Cell key={index} fill={entry.fill} />; })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">No pipeline data yet</p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Score Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scoreDistribution} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="score" tick={{ fontSize: 12, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={32}>
                {scoreDistribution.map(function(entry, index) { return <Cell key={index} fill={entry.fill} />; })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Top Qualified Tenders</h3>
          {topItems.length > 0 ? topItems : <p className="text-sm text-slate-400 py-4 text-center">No qualified tenders yet. Run scraping to discover tenders.</p>}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Organisation Sources</h3>
            <button onClick={function() { navigate('/tender-scout/sources'); }} className="text-xs text-primary hover:underline" data-testid="manage-sources-btn">Manage Sources</button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full">
              <thead><tr className="border-b border-slate-200"><th className="py-2 px-4 text-left text-xs font-medium text-slate-500">Organisation</th><th className="py-2 px-4 text-left text-xs font-medium text-slate-500">Status</th><th className="py-2 px-4 text-left text-xs font-medium text-slate-500">Count</th><th className="py-2 px-4 text-left text-xs font-medium text-slate-500">Last Run</th></tr></thead>
              <tbody>{sourceRows}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TenderScout;
